import React, { useState, useEffect } from 'react';
import { X, Search, BookOpen } from 'lucide-react';
import { BiblePicker } from './BiblePicker';
import { VerseReference, BibleVersion, Highlight } from '../types';
import { parseVerseReference } from '../utils/verseParser';
import { fetchBibleVerse, fetchBibleVerseRange, BIBLE_VERSIONS } from '../utils/bibleApi';
import { HighlightableText } from './HighlightableText';

interface ReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reference: VerseReference) => void;
  editingReference?: VerseReference | null;
  title: string;
}

export const ReferenceModal: React.FC<ReferenceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingReference,
  title
}) => {
  const [verseInput, setVerseInput] = useState('');
  const [verseText, setVerseText] = useState('');
  const [content, setContent] = useState('');
  const [bibleVersion, setBibleVersion] = useState<BibleVersion>('NLT');
  const [isLoadingVerse, setIsLoadingVerse] = useState(false);
  const [verseHighlights, setVerseHighlights] = useState<Highlight[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingReference) {
      setVerseInput(editingReference.display);
      setVerseText(editingReference.verseText || '');
      setContent(editingReference.content || '');
      setVerseHighlights(editingReference.verseHighlights || []);
    } else {
      setVerseInput('');
      setVerseText('');
      setContent('');
      setVerseHighlights([]);
    }
    setError('');
    setShowPicker(false);
  }, [editingReference, isOpen]);

  const handleFetchVerse = async (refOverride?: string) => {
    const parsed = parseVerseReference(refOverride ?? verseInput);
    if (!parsed) {
      setError('Invalid verse format. Use format like "John 3:16"');
      return;
    }

    setIsLoadingVerse(true);
    setError('');
    try {
      const text = parsed.endVerse
        ? await fetchBibleVerseRange(parsed.book, parsed.chapter, parsed.verse, parsed.endVerse, bibleVersion)
        : await fetchBibleVerse(parsed.book, parsed.chapter, parsed.verse, bibleVersion);
      setVerseText(text);
    } catch (err) {
      setError('Failed to fetch verse. Please check the reference and try again.');
    } finally {
      setIsLoadingVerse(false);
    }
  };

  const handleSave = () => {
    const parsed = parseVerseReference(verseInput);
    if (!parsed) {
      setError('Invalid verse format. Use format like "John 3:16" or "Isaiah 42:1-4"');
      return;
    }

    const reference: VerseReference = {
      ...parsed,
      verseText: verseText || undefined,
      verseHighlights: verseHighlights.length ? verseHighlights : undefined,
      content: content.trim() || undefined,
      linkedVerses: editingReference?.linkedVerses || [],
      isExpanded: editingReference?.isExpanded || false
    };

    onSave(reference);
    setVerseInput('');
    setVerseText('');
    setContent('');
    setVerseHighlights([]);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bible Version
            </label>
            <select
              value={bibleVersion}
              onChange={(e) => setBibleVersion(e.target.value as BibleVersion)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {BIBLE_VERSIONS.map((version) => (
                <option key={version.value} value={version.value}>
                  {version.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verse Reference
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={verseInput}
                onChange={(e) => setVerseInput(e.target.value)}
                placeholder="e.g., John 3:16 or Isaiah 42:1-4"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={() => setShowPicker(true)}
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                title="Browse books"
              >
                <BookOpen size={20} />
              </button>
              <button
                onClick={() => handleFetchVerse()}
                disabled={isLoadingVerse || !verseInput}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Fetch verse text"
              >
                <Search size={20} />
                {isLoadingVerse ? 'Loading...' : 'Fetch'}
              </button>
            </div>
            {showPicker && (
              <BiblePicker
                onSelect={(ref) => { setVerseInput(ref); setVerseText(''); setShowPicker(false); handleFetchVerse(ref); }}
                onClose={() => setShowPicker(false)}
              />
            )}
          </div>

          {verseText && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verse Text
                <span className="ml-2 text-xs font-normal text-gray-400">Select text to highlight</span>
              </label>
              <HighlightableText
                text={verseText}
                highlights={verseHighlights}
                onChange={setVerseHighlights}
                className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-gray-800 italic text-sm max-h-48 overflow-y-auto whitespace-pre-wrap"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Optional: Add your description/notes for this verse reference..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t">
          <button
            onClick={handleSave}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            {editingReference ? 'Update' : 'Add'} Reference
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
